import React, { useEffect, useState } from 'react';
import { Filter, RotateCcw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { masterService } from '../../../api/masterService';
import { configService } from '../../../api/configService';

export default function ProductAuditLog({ productId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userId, setUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Pagination
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0 });

  useEffect(() => {
    if (showFilter && users.length === 0) {
      setLoadingUsers(true);
      configService.getUsers()
        .then(res => {
          // If response is nested inside data (depends on how configService works)
          const data = res.data?.items || res.data || res || [];
          setUsers(Array.isArray(data) ? data : []);
        })
        .catch(err => console.error("Failed to load users", err))
        .finally(() => setLoadingUsers(false));
    }
  }, [showFilter, users.length]);

  const fetchLogs = (params = {}) => {
    if (!productId) return;
    setLoading(true);
    
    const query = {
      page: params.page !== undefined ? params.page : Number(meta.page),
      limit: params.limit !== undefined ? params.limit : Number(meta.limit),
      start_date: params.start_date !== undefined ? params.start_date : (startDate || undefined),
      end_date: params.end_date !== undefined ? params.end_date : (endDate || undefined),
      user_id: params.user_id !== undefined ? params.user_id : (userId || undefined)
    };

    masterService.products.getAuditLogs(productId, query)
      .then(res => {
        setLogs(res.data || []);
        if (res.meta) {
          setMeta(res.meta);
        }
      })
      .catch(err => console.error("Failed to load audit logs", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLogs();
  }, [productId]);

  const handleApplyFilter = () => {
    fetchLogs({ page: 1 });
  };

  const handleResetFilter = () => {
    setStartDate('');
    setEndDate('');
    setUserId('');
    fetchLogs({ page: 1, start_date: undefined, end_date: undefined, user_id: undefined });
  };

  const formatDateTime = (dateString) => {
    const d = new Date(dateString);
    const months = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const maxPage = Math.ceil(meta.total / meta.limit) || 1;

  return (
    <div className="bg-white rounded-xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] border border-slate-100 mt-6 overflow-hidden">
      <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white">
        <div className="flex items-center gap-4">
          <h3 className="text-[#1e3a8a] text-[18px] font-bold">Riwayat Perubahan</h3>
          <span className="text-xs text-slate-400 whitespace-nowrap bg-slate-100 px-2 py-1 rounded-md">
            {meta.total} aktivitas
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchLogs()}
            className="p-2.5 bg-white ring-1 ring-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors shadow-sm"
            title="Muat ulang">
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowFilter(!showFilter)}
            className={`p-2.5 rounded-lg ring-1 transition shadow-sm ${showFilter ? "ring-teal-300 text-teal-700 bg-teal-50" : "ring-slate-200 text-slate-600 bg-white hover:bg-slate-50"}`}
            title="Filter">
            <Filter className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showFilter && (
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Tanggal Mulai
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Tanggal Akhir
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Pengguna
              </label>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
              >
                <option value="">Semua Pengguna</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200/60 mt-4">
            <button
              onClick={handleResetFilter}
              className="px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
              Reset
            </button>
            <button
              onClick={handleApplyFilter}
              className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm shadow-teal-600/20">
              Terapkan
            </button>
          </div>
        </div>
      )}

      <div className="p-6">
        {loading ? (
          <p className="text-center text-sm text-slate-400 py-8 font-medium">Memuat log...</p>
        ) : logs.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-8 font-medium">Belum ada aktivitas</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 text-blue-400 text-[11px] uppercase tracking-wider">
                  <th className="pb-3 font-bold">Tanggal</th>
                  <th className="pb-3 font-bold">Aktivitas</th>
                  <th className="pb-3 font-bold">Detail</th>
                  <th className="pb-3 font-bold">Oleh</th>
                </tr>
              </thead>
              <tbody className="text-[#1e3a8a] font-medium text-[13px]">
                {logs.map((log) => (
                  <tr key={log.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 align-top pr-4 whitespace-nowrap">{formatDateTime(log.created_at)}</td>
                    <td className="py-4 align-top pr-4 whitespace-nowrap">{log.activity}</td>
                    <td className="py-4 align-top text-slate-600 font-normal leading-relaxed pr-4">{log.detail}</td>
                    <td className="py-4 align-top whitespace-nowrap">{log.performed_by || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {logs.length > 0 && (
        <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Tampilkan</span>
            <select
              value={meta.limit}
              onChange={(e) => fetchLogs({ page: 1, limit: Number(e.target.value) })}
              className="px-2 py-1.5 bg-white ring-1 ring-slate-200 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
            >
              {[10, 20, 50, 100].map((limit) => (
                <option key={limit} value={limit}>{limit}</option>
              ))}
            </select>
            <span className="text-sm text-slate-500">baris</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => fetchLogs({ page: 1 })}
              disabled={Number(meta.page) <= 1 || loading}
              className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
              title="Halaman pertama">
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => fetchLogs({ page: Math.max(1, Number(meta.page) - 1) })}
              disabled={Number(meta.page) <= 1 || loading}
              className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
              title="Halaman sebelumnya">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1 rounded-lg bg-teal-600 text-white text-sm font-semibold min-w-10 text-center shadow-sm shadow-teal-600/20">
              {meta.page}
            </span>
            <button
              onClick={() => fetchLogs({ page: Math.min(maxPage, Number(meta.page) + 1) })}
              disabled={Number(meta.page) >= maxPage || loading}
              className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
              title="Halaman berikutnya">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => fetchLogs({ page: maxPage })}
              disabled={Number(meta.page) >= maxPage || loading}
              className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-200 text-sm disabled:opacity-40 hover:bg-slate-50 inline-flex items-center justify-center transition"
              title="Halaman terakhir">
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
